import { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler, List, ListItem } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { getAdminMagicSecret } from '../../connector/auth';
import { TextButton } from 't-lib-ui/widgets';
import { chevronBackIcon, openIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { getAdminReports, updateAdminReport, adminGetApiServers, getAdminReportsPersonal } from '../../connector/api';
import { adminNotificationEngine } from '../../connector/admin-notification-engine';
import { REASON_LABELS } from '../../components/ReportPopup';
import { RestReport, RestApiServer, ReportStatus } from '../../schema-copy/api-schema';

const AdminReportsPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [reports, setReports] = useState<RestReport[]>([]);
  const [apiServers, setApiServers] = useState<RestApiServer[]>([]);
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'ALL'>('PENDING');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Report ids whose cross-instance PII fetch has been attempted, so we don't retry in a loop.
  const enrichAttempted = useRef<Set<string>>(new Set());

  const magic = getAdminMagicSecret() || adminSecret || '';

  useFlowFormGlobal();

  // The api-servers list (same on every instance) maps a report's owning instance to a base URL we
  // can target for its reporter PII. Loaded once; cross-instance reports are otherwise unreadable.
  useEffect(() => {
    const auth = appState.state.authData;
    if (!auth || !magic) return;
    (async () => {
      try {
        setApiServers(await adminGetApiServers(auth, magic));
      } catch (e) {
        console.error('Failed to load API servers:', e);
      }
    })();
  }, [appState.state.authData, magic]);

  useEffect(() => {
    registerKeyHandler('adminReportsPage', {
      'Escape': () => {
        goTo(routerCtx, '/a/admin/feeds');
        return true;
      },
    }, false, 0);
    return () => unregisterKeyHandler('adminReportsPage');
  }, [routerCtx]);

  const load = useCallback(async () => {
    const auth = appState.state.authData;
    if (!auth || !magic) return;
    setLoading(true);
    try {
      const r = await getAdminReports(auth, magic, statusFilter);
      // Fresh list: allow cross-instance PII to be (re-)fetched for the new items.
      enrichAttempted.current.clear();
      setReports(r.items);
    } catch (e) {
      console.error('Failed to load reports:', e);
    } finally {
      setLoading(false);
    }
  }, [appState.state.authData, magic, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Reports owned by another instance arrive with complete=false and no reporter PII. Fetch that PII
  // from each owning instance (grouped, one request per instance) and merge it in. Same-instance
  // reports are already complete (merged server-side), so the common single-instance case is a no-op.
  useEffect(() => {
    const auth = appState.state.authData;
    if (!auth || !magic || !apiServers.length) return;
    const todo = reports.filter(r => !r.complete && !enrichAttempted.current.has(r.id));
    if (!todo.length) return;

    const byInstance = new Map<string, string[]>();
    for (const r of todo) {
      enrichAttempted.current.add(r.id);
      const ids = byInstance.get(r.instance) ?? [];
      ids.push(r.id);
      byInstance.set(r.instance, ids);
    }

    (async () => {
      for (const [instance, ids] of Array.from(byInstance.entries())) {
        const server = apiServers.find(s => s.instance == instance);
        if (!server) continue; // unknown instance: leave incomplete; the UI flags it as unavailable
        try {
          const personal = await getAdminReportsPersonal(auth, magic, ids, server.url);
          const byId = new Map(personal.map(p => [p.id, p]));
          setReports(prev => prev.map(r => {
            const p = byId.get(r.id);
            return p ? { ...r, ...p, complete: true } : r;
          }));
        } catch (e) {
          console.error('Failed to load report details from instance', instance, e);
        }
      }
    })();
  }, [reports, apiServers, magic, appState.state.authData]);

  const resolve = useCallback(async (id: string, status: ReportStatus) => {
    const auth = appState.state.authData;
    if (!auth || !magic) return;
    setBusyId(id);
    try {
      const wasPending = reports.find(r => r.id == id)?.status == 'PENDING';
      const wasUrgent = reports.find(r => r.id == id)?.urgent ?? false;
      await updateAdminReport(auth, magic, id, { status });
      // Keep the sidebar badge in sync immediately (only a pending->resolved
      // transition reduces the pending count); the next poll reconciles exactly.
      if (wasPending) adminNotificationEngine.decrementReportsCount(wasUrgent);
      // Refresh so the resolved item drops out of the PENDING view.
      await load();
    } catch (e) {
      console.error('Failed to update report:', e);
    } finally {
      setBusyId(null);
    }
  }, [appState.state.authData, magic, load]);

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-reports"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin'>
              <div style={{ padding: '24px', maxWidth: '820px', margin: '0 auto' }}>
                <AdminHeader title="Content Reports" onExpand={onExpand} />
                <p style={{ marginBottom: '16px', color: 'var(--gray-400)' }}>
                  User and visitor reports of illegal or abusive feeds/posts (notice-and-action; see LEGAL.md).
                  Reports marked <strong style={{ color: 'var(--error-color)' }}>urgent</strong> (child-safety) are legally
                  time-sensitive — review immediately and follow the CSAM runbook.
                </p>

                <ButtonBar size="small">
                  <TextButton
                    label="Back"
                    icon={chevronBackIcon}
                    onClick={() => goTo(routerCtx, '/a/admin/feeds')}
                    focussable="flow"
                  />
                  <TextButton
                    label={statusFilter == 'PENDING' ? 'Showing: Pending' : 'Showing: All'}
                    onClick={() => setStatusFilter(statusFilter == 'PENDING' ? 'ALL' : 'PENDING')}
                    focussable="flow"
                    type="minor"
                  />
                </ButtonBar>

                <List>
                  {!loading && reports.length == 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                      {statusFilter == 'PENDING' ? 'No pending reports. All clear.' : 'No reports.'}
                    </div>
                  )}
                  {reports.map((r) => (
                    <ListItem key={r.id} focussable="normal">
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>
                            {r.urgent && (
                              <span style={{ color: 'var(--error-color)', marginRight: '8px', textTransform: 'uppercase', fontSize: '0.8em' }}>
                                Urgent
                              </span>
                            )}
                            {REASON_LABELS[r.reason] ?? r.reason}
                            {r.status != 'PENDING' && (
                              <span style={{ color: 'var(--gray-500)', fontWeight: 400, marginLeft: '8px', fontSize: '0.85em' }}>
                                ({r.status == 'ACTIONED' ? 'confirmed' : r.status.toLowerCase()})
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', marginTop: '4px' }}>
                            Feed {r.feedId}{r.postIndex != null ? ` · post #${r.postIndex}` : ''} · instance {r.instance} · {formatDate(r.createdAt)}
                          </div>
                          {r.complete && r.details && (
                            <div style={{ fontSize: '0.9em', color: 'var(--gray-200)', marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                              {r.details}
                            </div>
                          )}
                          <div style={{ fontSize: '0.8em', color: 'var(--gray-500)', marginTop: '4px' }}>
                            {r.complete
                              ? `Reporter: ${[
                                  r.reporterUserId ? `user ${r.reporterUserId}` : null,
                                  r.reporterEmail || null,
                                  r.reporterIp ? `IP ${r.reporterIp}` : null,
                                ].filter(Boolean).join(' · ') || 'anonymous'}`
                              : `Reporter details on instance ${r.instance}${apiServers.some(s => s.instance == r.instance) ? '…' : ' (unavailable)'}`}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                          <TextButton
                            label="Open feed"
                            icon={openIcon}
                            onClick={() => goTo(routerCtx, `/a/admin/feed/${r.feedId}`)}
                            focussable="flow"
                            size="small"
                            type="minor"
                          />
                          {r.status == 'PENDING' && (
                            <>
                              <TextButton
                                label="Confirm"
                                onClick={() => resolve(r.id, 'ACTIONED')}
                                focussable="flow"
                                size="small"
                                type="major"
                                enabled={busyId == r.id ? 'disabled' : 'enabled'}
                              />
                              <TextButton
                                label="Dismiss"
                                onClick={() => resolve(r.id, 'DISMISSED')}
                                focussable="flow"
                                size="small"
                                type="minor"
                                enabled={busyId == r.id ? 'disabled' : 'enabled'}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </ListItem>
                  ))}
                </List>
              </div>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminReportsPage;
